import React, { useState, useEffect } from 'react';
import { Loader2, Search, FileText, ArrowUp, ArrowDown, TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { ocAmendmentAPI } from '../../services/api';

const AmendedOCsReport = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [search, statusFilter]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const data = await ocAmendmentAPI.report(params);
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (err) {
      console.error('Failed to load amended OCs report:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="amended-ocs-report">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Total Amendments</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{summary.total_amendments}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Unique OCs</p>
            <p className="text-xl font-bold text-purple-600 mt-1">{summary.unique_ocs_amended}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Value Increased</p>
            <p className="text-xl font-bold text-green-600 mt-1 flex items-center gap-1">
              <TrendingUp className="w-4 h-4" /> {(summary.total_value_increase || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Value Decreased</p>
            <p className="text-xl font-bold text-red-600 mt-1 flex items-center gap-1">
              <TrendingDown className="w-4 h-4" /> {(summary.total_value_decrease || 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-[10px] font-medium text-gray-500 uppercase">Net Change</p>
            <p className={`text-xl font-bold mt-1 ${(summary.net_value_change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(summary.net_value_change || 0) >= 0 ? '+' : ''}{(summary.net_value_change || 0).toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by OC number, customer, requester..."
            className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-200 focus:outline-none"
            data-testid="amended-ocs-search"
          />
        </div>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[{ label: 'Approved', value: '' }, { label: 'Pending', value: 'pending' }, { label: 'Rejected', value: 'rejected' }].map(f => (
            <button key={f.value} onClick={() => setStatusFilter(f.value)} data-testid={`report-filter-${f.value || 'approved'}`}
              className={`px-3 py-1.5 text-[10px] font-semibold rounded-md transition-all ${statusFilter === f.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No amended OCs found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[9px] font-bold text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-2.5">OC #</th>
                  <th className="text-left px-3 py-2.5">Customer</th>
                  <th className="text-center px-3 py-2.5">Rev</th>
                  <th className="text-right px-3 py-2.5">Original Value</th>
                  <th className="text-right px-3 py-2.5">Amended Value</th>
                  <th className="text-right px-3 py-2.5">Change</th>
                  <th className="text-left px-3 py-2.5">Amended By</th>
                  <th className="text-left px-3 py-2.5">Approved By</th>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <React.Fragment key={item.amendment_id}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer" onClick={() => setExpandedRow(expandedRow === item.amendment_id ? null : item.amendment_id)}>
                      <td className="px-4 py-2.5">
                        <span className="text-xs font-bold" style={{ color: '#24549c' }}>{item.oc_number}</span>
                        {item.current_status && (
                          <span className="ml-1.5 text-[9px] text-gray-400">{item.current_status}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-800">{item.customer_name}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-purple-100 text-purple-700">Rev-{item.revision_applied}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-right text-gray-600">{(item.original_total || 0).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-xs text-right font-semibold text-gray-800">{(item.proposed_total || 0).toLocaleString()}</td>
                      <td className="px-3 py-2.5 text-xs text-right">
                        <span className={`font-bold flex items-center justify-end gap-0.5 ${item.value_change > 0 ? 'text-green-600' : item.value_change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {item.value_change > 0 && <ArrowUp className="w-3 h-3" />}
                          {item.value_change < 0 && <ArrowDown className="w-3 h-3" />}
                          {Math.abs(item.value_change).toLocaleString()}
                          <span className="text-[9px] ml-0.5">({item.value_change_pct}%)</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{item.requested_by}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">{item.reviewed_by || '-'}</td>
                      <td className="px-3 py-2.5 text-[10px] text-gray-400">{new Date(item.requested_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</td>
                      <td className="px-2 py-2.5">
                        {expandedRow === item.amendment_id ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                      </td>
                    </tr>
                    {expandedRow === item.amendment_id && (
                      <tr>
                        <td colSpan={10} className="px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Reason</p>
                              <p className="text-xs text-gray-700">{item.reason}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Review Notes</p>
                              <p className="text-xs text-gray-700">{item.review_notes || 'No notes'}</p>
                            </div>
                          </div>
                          {item.changes_summary?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-[10px] font-bold text-gray-500 uppercase mb-1">Changes</p>
                              <div className="flex flex-wrap gap-2">
                                {item.changes_summary.map((c, ci) => (
                                  <span key={ci} className="px-2 py-1 bg-white border border-gray-200 rounded-lg text-[10px] text-gray-700">
                                    <strong>{c.item}:</strong> {c.changes?.join(', ')}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmendedOCsReport;
